import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext'; // Votre hook existant
import orderService, { Order, OrderStats, PaymentConfirmation } from '../services/orderService';
import { 
  ShoppingBag, 
  Eye, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Filter,
  RefreshCw,
  X,
  DollarSign
} from 'lucide-react';

const ReceivedOrdersPage: React.FC = () => {
  const { user } = useAuth(); // Votre hook existant
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  // ✅ NOUVEAU - États pour modal de confirmation de paiement
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState<Order | null>(null);
  const [confirmationData, setConfirmationData] = useState<PaymentConfirmation>({
    amount_received: 0,
    payment_method: 'mobile_money',
    payment_reference: '',
    notes: ''
  });
  const [confirmingPayment, setConfirmingPayment] = useState(false);

useEffect(() => {
    // ✅ CORRECTION: Vérifier si farmer et avoir l'ID
    if (user?.is_farmer && user.id) {
      loadData();
    } else if (user && !user.is_farmer) {
      // Rediriger les non-farmers
      window.location.href = '/';
    }
  }, [user?.id, user?.is_farmer, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const statusFilter = selectedStatus === 'all' ? undefined : selectedStatus;
      
      const [ordersData, statsData] = await Promise.all([
        orderService.getFarmerOrders(user!.id, statusFilter),
        orderService.getFarmerOrderStats(user!.id)
      ]);
      
      setOrders(ordersData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId: number) => {
    try {
      const orderDetails = await orderService.getOrderFarmerDetails(orderId, user!.id);
      setSelectedOrder(orderDetails);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error loading order details:', error);
    }
  };

  const handleStatusUpdate = async (orderId: number, newStatus: string) => {
    try {
      setUpdatingStatus(orderId);
      await orderService.updateOrderStatus(orderId, user!.id, newStatus);
      await loadData(); // Recharger les données
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // ✅ NOUVEAU - Fonction avec confirmation pour l'annulation
  const handleCancelOrder = (orderId: number) => {
    if (confirm('Êtes-vous sûr de vouloir annuler cette commande ? Cette action est irréversible.')) {
      handleStatusUpdate(orderId, 'cancelled');
    }
  };

  // ✅ NOUVEAU - Gérer la confirmation de paiement
  const handleConfirmPayment = (order: Order) => {
    setConfirmingOrder(order);
    setConfirmationData({
      amount_received: order.farmer_total || order.total_price,
      payment_method: 'mobile_money',
      payment_reference: '',
      notes: ''
    });
    setShowConfirmModal(true);
  };

  // ✅ NOUVEAU - Soumettre la confirmation de paiement
  const handleSubmitConfirmation = async () => {
    if (!confirmingOrder) return;

    try {
      setConfirmingPayment(true);
      
      await orderService.confirmPaymentReceived(confirmingOrder.id, confirmationData);
      
      // Recharger les données
      await loadData();
      
      setShowConfirmModal(false);
      setConfirmingOrder(null);
      
      alert(`Paiement confirmé avec succès pour la commande #${confirmingOrder.id} !`);
      
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('Erreur lors de la confirmation du paiement. Veuillez réessayer.');
    } finally {
      setConfirmingPayment(false);
    }
  };

  // ✅ MODIFIÉ - Fonction pour obtenir les actions disponibles selon le statut
  const getOrderActions = (order: Order) => {
    const actions = [];
    
    // Bouton "Voir" toujours présent
    actions.push(
      <button
        key="view"
        onClick={() => handleViewOrder(order.id)}
        className="text-olive hover:text-terracotta flex items-center gap-1"
      >
        <Eye className="h-4 w-4" />
        Voir
      </button>
    );

    // Si c'est en attente de paiement, boutons "Confirmer" et "Annuler"
    if (order.status === 'payment_pending') {
      actions.push(
        <button
          key="confirm"
          onClick={() => handleConfirmPayment(order)}
          disabled={updatingStatus === order.id}
          className="ml-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition duration-300 flex items-center gap-1"
        >
          {updatingStatus === order.id ? (
            '...'
          ) : (
            <>
              <CheckCircle className="h-3 w-3" />
              Confirmer
            </>
          )}
        </button>
      );
      
      // ✅ NOUVEAU - Bouton Annuler pour les commandes en attente
      actions.push(
        <button
          key="cancel"
          onClick={() => handleCancelOrder(order.id)}
          disabled={updatingStatus === order.id}
          className="ml-2 px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition duration-300 flex items-center gap-1"
        >
          {updatingStatus === order.id ? (
            '...'
          ) : (
            <>
              <XCircle className="h-3 w-3" />
              Annuler
            </>
          )}
        </button>
      );
    } else {
      // Pour les autres statuts, afficher les transitions normales
      const nextStatuses = orderService.getNextValidStatuses(order.status);
      nextStatuses.forEach((nextStatus) => {
        actions.push(
          <button
            key={nextStatus}
            onClick={() => handleStatusUpdate(order.id, nextStatus)}
            disabled={updatingStatus === order.id}
            className="ml-2 px-2 py-1 text-xs bg-olive text-white rounded hover:bg-terracotta disabled:opacity-50 transition duration-300"
          >
            {updatingStatus === order.id ? '...' : orderService.getStatusLabel(nextStatus)}
          </button>
        );
      });
    }

    return actions;
  };

  const getStatusBadge = (status: string) => (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${orderService.getStatusColor(status)}`}>
      {orderService.getStatusLabel(status)}
    </span>
  );

  const StatusCard = ({ title, value, icon: Icon, color }: any) => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate">{title}</p>
          <p className="text-2xl font-bold text-terracotta">{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-beige min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate mb-2">Commandes Reçues</h1>
        <p className="text-gray-600">Gérez les commandes de vos produits</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatusCard
            title="Total des ventes"
            value={orderService.formatPrice(stats.total_sales)}
            icon={TrendingUp}
            color="text-olive"
          />
          <StatusCard
            title="Commandes totales"
            value={stats.total_orders}
            icon={ShoppingBag}
            color="text-olive"
          />
          <StatusCard
            title="Commandes récentes"
            value={stats.recent_orders}
            icon={Clock}
            color="text-orange-500"
          />
          <StatusCard
            title="En attente"
            value={stats.orders_by_status.pending || 0}
            icon={Clock}
            color="text-orange-500"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-slate">Filtrer par statut:</span>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tous les statuts</option>
            <option value="payment_pending">En attente de paiement</option>
            <option value="payment_confirmed">Paiement confirmé</option>
            <option value="preparing">En préparation</option>
            <option value="ready">Prêtes</option>
            <option value="shipped">Expédiées</option>
            <option value="delivered">Livrées</option>
          </select>
          <button
            onClick={loadData}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-olive text-white rounded-md hover:bg-terracotta transition duration-300"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-slate">
            Liste des commandes ({orders.length})
          </h2>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate mb-2">Aucune commande</h3>
            <p className="text-gray-600">
              {selectedStatus === 'all' 
                ? "Vous n'avez pas encore reçu de commandes." 
                : `Aucune commande avec le statut "${orderService.getStatusLabel(selectedStatus)}".`
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commande #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate">
                      #{order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {orderService.formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate">
                      Client #{order.user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate">
                      {orderService.formatPrice(order.total_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {getOrderActions(order)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate">Détails de la commande #{selectedOrder.order.id}</h2>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-gray-400 hover:text-slate"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Customer Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-slate mb-2">Informations client</h3>
                <p><strong>Nom:</strong> {selectedOrder.customer.name}</p>
                {selectedOrder.customer.email && (
                  <p><strong>Email:</strong> {selectedOrder.customer.email}</p>
                )}
                {selectedOrder.customer.phone && (
                  <p><strong>Téléphone:</strong> {selectedOrder.customer.phone}</p>
                )}
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate mb-3">Produits commandés</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item: any) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-slate">Produit #{item.product_id}</p>
                        <p className="text-sm text-gray-600">Quantité: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate">{orderService.formatPrice(item.price_per_unit)}</p>
                        <p className="text-sm text-gray-600">
                          Total: {orderService.formatPrice(item.quantity * item.price_per_unit)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Total */}
              <div className="flex justify-between items-center text-lg font-bold border-t pt-4">
                <span className="text-slate">Total de vos produits:</span>
                <span className="text-terracotta">{orderService.formatPrice(selectedOrder.farmer_total)}</span>
              </div>

              {/* Status Actions */}
              <div className="mt-6 flex gap-2">
                {selectedOrder.order.status === 'payment_pending' ? (
                  <>
                    <button
                      onClick={() => {
                        setShowOrderModal(false);
                        handleConfirmPayment(selectedOrder.order);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition duration-300 flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Confirmer le paiement
                    </button>
                    {/* ✅ NOUVEAU - Bouton Annuler dans le modal */}
                    <button
                      onClick={() => {
                        setShowOrderModal(false);
                        handleCancelOrder(selectedOrder.order.id);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300 flex items-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Annuler la commande
                    </button>
                  </>
                ) : (
                  orderService.getNextValidStatuses(selectedOrder.order.status).map((nextStatus) => (
                    <button
                      key={nextStatus}
                      onClick={() => {
                        handleStatusUpdate(selectedOrder.order.id, nextStatus);
                        setShowOrderModal(false);
                      }}
                      className="px-4 py-2 bg-olive text-white rounded hover:bg-terracotta transition duration-300"
                    >
                      Marquer comme {orderService.getStatusLabel(nextStatus)}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NOUVEAU - Modal de confirmation de paiement */}
      {showConfirmModal && confirmingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate">
                  Confirmer le paiement
                </h3>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-slate mb-2">Commande #{confirmingOrder.id}</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Client:</span> Client #{confirmingOrder.user_id}</p>
                  <p><span className="font-medium">Montant total:</span> {orderService.formatPrice(confirmingOrder.farmer_total || confirmingOrder.total_price)}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Montant reçu *
                  </label>
                  <input
                    type="number"
                    value={confirmationData.amount_received}
                    onChange={(e) => setConfirmationData(prev => ({
                      ...prev,
                      amount_received: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Montant en FCFA"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Mode de paiement
                  </label>
                  <select
                    value={confirmationData.payment_method}
                    onChange={(e) => setConfirmationData(prev => ({
                      ...prev,
                      payment_method: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                  >
                    <option value="mobile_money">Mobile Money</option>
                    <option value="cash">Espèces</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Référence de transaction
                  </label>
                  <input
                    type="text"
                    value={confirmationData.payment_reference}
                    onChange={(e) => setConfirmationData(prev => ({
                      ...prev,
                      payment_reference: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Référence MTN MoMo ou autre"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Notes (optionnel)
                  </label>
                  <textarea
                    value={confirmationData.notes}
                    onChange={(e) => setConfirmationData(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Commentaires sur le paiement..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={confirmingPayment}
                  className="flex-1 bg-gray-200 text-slate px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitConfirmation}
                  disabled={confirmingPayment || confirmationData.amount_received <= 0}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {confirmingPayment ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Confirmation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Confirmer le paiement
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedOrdersPage;